import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

import {
  trans,
  getPagePath,
  clientErrorTracker,
  useSoyuzFlag
} from '@spotahome/soyuz/client';

import * as yup from 'yup';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { useCookie } from '@spotahome/ui-library';
import ReCaptcha from '@spotahome/ui-library/src/tenant/components/ReCaptcha';

import { useStateMachineContext } from '@spotahome/landings-common/src/context/StateMachineContext';

import graphqlClient from '@spotahome/landings-common/src/graphql/client';

import createZendeskTicket, {
  createZendeskTicketLogged
} from '@spotahome/ui-library/src/tenant/mutations/zendesk';

import FormProvider from '@spotahome/landings-common/src/components/FormProvider';
import FormError from '@spotahome/landings-common/src/components/FormError';

import { TENANT_REASONS, FORM_FIELDS } from '../../constants';

import ContactUsHeader from '../ContactUsHeader';

import './ContactForm.scss';
import { applyActions } from '../../utils/actions';

const FORM_COMPONENTS = {
  text: {
    component: FormProvider.Input,
    props: {
      type: 'text'
    }
  },
  number: {
    component: FormProvider.Input,
    props: {
      type: 'number'
    }
  },
  email: {
    component: FormProvider.Input,
    props: {
      type: 'email'
    }
  },
  date: {
    component: FormProvider.Date
  },
  textarea: {
    component: FormProvider.Input,
    props: {
      rows: 4,
      type: 'textarea'
    }
  },
  select: {
    component: FormProvider.Select
  },
  cityAutocompleter: {
    component: FormProvider.CityAutocompleter
  },
  phone: {
    component: FormProvider.PhoneInput
  },
  dependentSelectors: {
    component: FormProvider.DependentSelectors
  }
};

const composeFormSchema = (formFields, conditions) => {
  const schemaObject = formFields.reduce((acc, fieldId) => {
    const fieldCondition = conditions[fieldId];
    if (fieldCondition) {
      const { dependsOn, compare } = fieldCondition;
      acc[fieldId] = FORM_FIELDS[fieldId].schema.when(dependsOn, {
        is: compare,
        then: yup.string().ensure().required('contact-us.required-field')
      });
    } else {
      acc[fieldId] = FORM_FIELDS[fieldId].schema;
    }

    return acc;
  }, {});

  return yup.object().shape(schemaObject);
};

const ContactForm = ({ isPublicForm = false }) => {
  const [recaptchaPassed, setRecaptchaPassed] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    send,
    payload: { role, reasonId }
  } = useStateMachineContext();
  const isShowDeactivateAccountEnabled = useSoyuzFlag(
    'ShowDeactivateAccount'
  ).isOn();

  const { getCookie } = useCookie();

  const {
    helpCenterArticles,
    linkTo,
    fields: formFields,
    sosNumber,
    conditions = {},
    postActions = []
  } = TENANT_REASONS[reasonId];

  const formSchema = composeFormSchema(formFields, conditions);

  const baseLocoKey = `contact-us.${role}`;

  const scrollTop = () => window && window.scrollTo({ top: 0 });

  const handleFormSubmit = async values => {
    const {
      city,
      referenceCode,
      bookingId,
      name,
      email,
      rentableUnitId,
      moveInDate,
      comment,
      numberProperties,
      phoneNumber,
      phoneNumberOptional,
      budget,
      device,
      browser,
      tenantProfile,
      neighbourhood,
      propertyType,
      idNumber,
      address,
      listingModificationType,
      newContactDetails,
      unpublishReason,
      cancellationReason,
      doubtMotive,
      doubtSubmotive
    } = values;

    setSubmitError(false);
    setLoading(true);

    if (doubtMotive && doubtSubmotive) {
      SoyuzAnalytics.sendGA4Event('send', {
        section: 'listing-doubts',
        motive: doubtMotive,
        submotive: doubtSubmotive
      });
    }

    const input = {
      reason: reasonId,
      role,
      phoneNumber: phoneNumber || phoneNumberOptional,
      numberProperties,
      referenceCode,
      bookingId,
      city: city ? city.id : null,
      country: city ? city.country.countryId : null,
      rentableUnitId,
      moveInDate,
      comment,
      budget,
      device,
      browser,
      tenantProfile,
      neighbourhood,
      propertyType,
      idNumber,
      address,
      listingModificationType,
      newContactDetails,
      unpublishReason,
      cancellationReason,
      doubtMotive,
      doubtSubmotive,
      sourceSegment: getCookie('tenantSupportSource') || null
    };

    try {
      if (isPublicForm) {
        await createZendeskTicket(graphqlClient())({
          input: {
            ...input,
            name,
            email
          }
        });
      } else {
        await createZendeskTicketLogged(graphqlClient())({ input });
      }

      if (isShowDeactivateAccountEnabled) {
        await applyActions(postActions);
      }

      setLoading(false);
      send('NEXT', { ...values, sosNumber });
      scrollTop();
    } catch (error) {
      setLoading(false);
      setSubmitError(true);
      clientErrorTracker.error({ error, source: 'ContactForm' });
    }
  };

  const getArticleCopy = () =>
    helpCenterArticles.length > 1
      ? trans(`${baseLocoKey}.hint.help_center.multiple_articles`)
      : trans(`${baseLocoKey}.hint.help_center.single_article`);

  const getArticleList = () =>
    helpCenterArticles.map(({ id, title }) => {
      return (
        <li key={id}>
          <a href={getPagePath(id)} target="_blank" rel="noreferrer">
            {trans(title)}
          </a>
        </li>
      );
    });

  const onCaptchaSuccess = () => {
    setRecaptchaPassed(true);
  };

  const onCaptchaExpired = () => {
    setRecaptchaPassed(false);
  };

  return (
    <div className="contact-form">
      <ContactUsHeader
        title={trans(`${baseLocoKey}.${reasonId}`)}
        onBack={() => send('BACK')}
      />

      {linkTo && (
        <div className={'contact-form__description'}>
          <span>{trans(`${baseLocoKey}.hint.go_to_link.${linkTo}`)}</span>
          <div className="contact-form__article-list-hint">
            <a href={getPagePath(linkTo)} target="_blank" rel="noreferrer">
              {trans(linkTo)}
            </a>
          </div>
        </div>
      )}

      {helpCenterArticles && !!helpCenterArticles.length && (
        <div className={'contact-form__description'}>
          <span>{getArticleCopy()}</span>
          <ul className="contact-form__article-list-hint">
            {getArticleList()}
          </ul>
        </div>
      )}

      {!!formFields.length && (
        <FormProvider
          disabled={!recaptchaPassed}
          schema={formSchema}
          onSubmit={handleFormSubmit}
        >
          {({ values }) => (
            <Fragment>
              {formFields
                .map(fieldId => ({
                  id: fieldId,
                  ...FORM_FIELDS[fieldId]
                }))
                .filter(fieldConfig => {
                  const fieldCondition = conditions[fieldConfig.id];
                  if (fieldCondition) {
                    const { compare, dependsOn } = fieldCondition;
                    const compareArgs = dependsOn.map(dep => values[dep]);
                    return compare(...compareArgs);
                  }
                  return true;
                })
                .map(({ id, type, props }) => {
                  const Component = FORM_COMPONENTS[type].component;
                  return (
                    <Component
                      id={id}
                      key={id}
                      name={id}
                      title={trans(`contact-us.field.${id}.title`)}
                      placeholder={trans(`contact-us.field.${id}.placeholder`)}
                      {...FORM_COMPONENTS[type].props}
                      {...props}
                    />
                  );
                })}
              <div className={'contact-form__recaptcha'}>
                <ReCaptcha
                  onExpired={onCaptchaExpired}
                  onChange={onCaptchaSuccess}
                />
              </div>
              <div className={'contact-form__submit-container'}>
                <div className={'contact-form__submit-button'}>
                  <FormProvider.Submit isLoading={loading}>
                    {loading
                      ? trans('loading')
                      : trans('contact-us.form.submit')}
                  </FormProvider.Submit>
                </div>
                {submitError && (
                  <div className={'contact-form__submit-error'}>
                    <FormError error={trans('contact-us.form-error')} />
                  </div>
                )}
              </div>
            </Fragment>
          )}
        </FormProvider>
      )}
    </div>
  );
};

ContactForm.propTypes = {
  isPublicForm: PropTypes.bool
};

export default ContactForm;
